// Converts only the parts of a document that one rule matches, for the rule editor's preview.
import mammoth from "mammoth";
import { inlineImages } from "./convert.js";
import { toStyleMap } from "./stylemap.js";

// Inserted between non-adjacent matches so mammoth doesn't merge them, then split on.
const SEPARATOR = "\u2063mammoth-press-separator\u2063";

const RUN_FLAGS = {
  b: "isBold",
  i: "isItalic",
  u: "isUnderline",
  strike: "isStrikethrough",
  "small-caps": "isSmallCaps",
  "all-caps": "isAllCaps",
};

function nameMatches(rule, name) {
  if (!rule.style) return !name;
  if (!name) return false;
  const actual = name.toLowerCase();
  const wanted = rule.style.toLowerCase();
  return rule.prefix ? actual.startsWith(wanted) : actual === wanted;
}

function descendants(element, type) {
  const found = [];
  (function walk(node) {
    for (const child of node.children ?? []) {
      if (child.type === type) found.push(child);
      walk(child);
    }
  })(element);
  return found;
}

export function elementMatches(rule, element) {
  const { match } = rule;
  if (match === "table") return element.type === "table" && nameMatches(rule, element.styleName);
  if (element.type !== "paragraph") return false;
  if (match === "p") return nameMatches(rule, element.styleName);
  const list = /^(ul|ol)([1-5])$/.exec(match);
  if (list) {
    const numbering = element.numbering;
    return Boolean(numbering) && numbering.isOrdered === (list[1] === "ol") && Number(numbering.level) + 1 === Number(list[2]);
  }
  const runs = descendants(element, "run");
  if (match === "r") return runs.some((run) => nameMatches(rule, run.styleName));
  if (match === "highlight") return runs.some((run) => run.highlight);
  const flag = RUN_FLAGS[match];
  return flag ? runs.some((run) => run[flag]) : false;
}

// A top-level body element matches directly, or (for a table) because something inside it matches.
function matchesWithin(rule, element) {
  if (elementMatches(rule, element)) return true;
  return element.type === "table" && rule.match !== "table"
    && descendants(element, "paragraph").some((paragraph) => elementMatches(rule, paragraph));
}

function separatorParagraph() {
  return {
    type: "paragraph",
    children: [{
      type: "run",
      children: [{ type: "text", value: SEPARATOR }],
      styleId: null, styleName: null, isBold: false, isUnderline: false, isItalic: false,
      isStrikethrough: false, isAllCaps: false, isSmallCaps: false, verticalAlignment: "baseline",
      font: null, fontSize: null, highlight: null,
    }],
    styleId: null, styleName: null, numbering: null, alignment: null,
    indent: { start: null, end: null, firstLine: null, hanging: null },
  };
}

export async function extractMatches(data, rule, profile, { limit = 5 } = {}) {
  let total = 0;
  const transformDocument = (document) => {
    const kept = [];
    let previousKept = false;
    for (const child of document.children) {
      const matches = matchesWithin(rule, child);
      if (matches) total += 1;
      const keep = matches && total <= limit;
      if (keep && !previousKept && kept.length) kept.push(separatorParagraph());
      if (keep) kept.push(child);
      previousKept = keep;
    }
    return { ...document, children: kept };
  };
  const result = await mammoth.convertToHtml(
    { arrayBuffer: data, buffer: data },
    { styleMap: toStyleMap(profile), transformDocument, convertImage: inlineImages },
  );
  const parts = result.value.split(`<p>${SEPARATOR}</p>`).filter(Boolean);
  return { html: parts.join("\n"), total, shown: Math.min(total, limit) };
}
